import type { Post, PostSummary, Comment, CreatePostInput, UpdatePostInput } from './types';

// In dev: http://localhost:8080 (set via REACT_APP_API_URL in docker-compose)
// In prod: '' (same-origin via CloudFront /api/* behaviour)
const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 204) return undefined as unknown as T;

  const data = await res.json();
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
  }
  return data as T;
}

export const api = {
  posts: {
    list: (token?: string | null) =>
      request<PostSummary[]>('/api/posts', {}, token),

    get: (slug: string, token?: string | null) =>
      request<Post>(`/api/posts/${slug}`, {}, token),

    create: (input: CreatePostInput, token: string) =>
      request<Post>('/api/posts', { method: 'POST', body: JSON.stringify(input) }, token),

    update: (slug: string, input: UpdatePostInput, token: string) =>
      request<Post>(`/api/posts/${slug}`, { method: 'PUT', body: JSON.stringify(input) }, token),

    delete: (slug: string, token: string) =>
      request<void>(`/api/posts/${slug}`, { method: 'DELETE' }, token),
  },

  comments: {
    list: (slug: string) =>
      request<Comment[]>(`/api/posts/${slug}/comments`),

    create: (slug: string, content: string, token: string) =>
      request<Comment>(
        `/api/posts/${slug}/comments`,
        { method: 'POST', body: JSON.stringify({ content }) },
        token,
      ),

    delete: (slug: string, commentId: string, token: string) =>
      request<void>(`/api/posts/${slug}/comments/${commentId}`, { method: 'DELETE' }, token),
  },
};
